import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  Box,
  Button,
} from '@mui/material';
import Tooltip from '../../../../Components/Atoms/Tooltip';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { ModalLoader } from '../../../../Components/Atoms/ModalLoader';
import { Toggle } from '../../../../Components/Atoms/TabGroup';
import { ScreenGreeting } from '../../../../Components/Molecules';
import { InvoiceDetail } from '../../../../Components/Molecules/Anticipation/InvoiceDetail';
import { InvoiceSummary } from '../../../../Components/Molecules/Anticipation/InvoiceSummary';
import { ServiceInvoiceDetail } from '../../../../Components/Molecules/Anticipation/ServiceInvoiceDetail';
import { useInvoices, useInvoiceValidate } from '../../../../Hooks/Anticipation';
import { useToast } from '../../../../Hooks/toast';
import useCallAxios from '../../../../Hooks/useCallAxios';

moment.tz.setDefault('America/Sao_Paulo');

export const AddInvoice = () => {
  const MAX_FILES = 20;
  const [type, setType] = useState<'PRODUTO' | 'SERVICO'>('PRODUTO');
  const [importedInvoices, setImportedInvoices] = useState<any[]>([]);
  const [limit, setLimit] = useState<number>(0);
  const [canEditDueDate, setCanEditDueDate] = useState<boolean>(false);

  const successInvoicesRef = useRef<string[]>([]);
  const { addToast } = useToast();
  const requestsTotalRef = useRef(0);
  const responsesReceivedRef = useRef(0);
  const [returnLoading, setReturnLoading] = useState(false);

  const history = useHistory();
  const contextStatus = "invoiceStatus";

  const {
    fetchGET: fetchStatus,
    requestError: errorStatus,
    requestData: statusData,
    requestLoading: loadingStatus,
  } = useCallAxios({ context: contextStatus });

  useEffect(() => {
    fetchStatus({ showToast: false });
  }, [contextStatus]);

  useEffect(() => {
    if ((statusData as any).data && !loadingStatus.GET) {
      setLimit((statusData as any).data.limit);
      setCanEditDueDate((statusData as any).data.allowDueDateChange || false);
    }
  }, [statusData, loadingStatus.GET]);

  useEffect(() => {
    if (errorStatus.GET != null && errorStatus.GET)
      history.push({
        pathname: "/anticipation",
      });
  }, [errorStatus.GET]);

  const {
    success,
    state,
    loading,
    handlePOST: validateInvoices,
  } = useInvoiceValidate();

  const {
    success: successInvoice,
    error: errorInvoice,
    loading: loadingInvoice,
    handlePOST: importInvoices,
    state: stateInvoice,
  } = useInvoices();

  const {
    fetchDELETE: handleDelete,
  } = useCallAxios({ context: 'invoices' });

  const addInvoiceIfNotExists = (invoiceState: any) => {
    const alreadyExists = importedInvoices.some(
      (item) => item.id === invoiceState.id
    );

    if (alreadyExists) return;

    const enriched = {
      ...invoiceState,
      fileName: invoiceState.fileName ?? 'NF-e.xml',
      pdf: null
    };

    setImportedInvoices((prev) => [...prev, enriched]);
  };

  useEffect(() => {
    if (!success || !state) return;

    addInvoiceIfNotExists(state);
  }, [state, success]);

  const fileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    if (importedInvoices.length + filesArray.length > MAX_FILES) {
      addToast({
        title: "Limite de importação.",
        description: `O limite de importação de nota é de ${MAX_FILES} arquivos.`,
        type: "error",
      });
      e.target.value = '';
      return;
    }

    if (type === 'PRODUTO') {
      for (const file of filesArray) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          validateInvoices(formData);
        } catch (err) {
          console.error('Erro ao importar nota:', err);
        }
      }
    }
    if (type === 'SERVICO') {
      for (const file of filesArray) {
        try {
          const fileName = file.name;

          const fileId = uuidv4();
          const newFile = {
            id: fileId,
            fileName: fileName,
            pdf: file,
          };

          const alreadyExists = importedInvoices.some(
            (item) => item.fileName === newFile.fileName
          );

          if (alreadyExists) return;

          setImportedInvoices((prev) => [...prev, newFile]);
        } catch (err) {
          console.error('Erro ao importar nota:', err);
        }
      }
    }

    e.target.value = '';
  };

  // Validação detalhada das notas de SERVIÇO para feedback ao usuário
  const collectServiceInvoiceErrors = () => {
    const allErrors: Array<{ id: string; fileName: string; errors: string[] }> = [];

    importedInvoices.forEach((invoice) => {
      if (type !== 'SERVICO') return;

      const errors: string[] = [];
      const { verificationCode, number, customer, vOrig, dup, issueDate } = invoice || {};

      // Campos simples
      if (!verificationCode || verificationCode.toString().trim() === '') errors.push('Código de Verificação/Autenticidade');
      const numberOk = number !== '' && !isNaN(Number(number)) && Number(number) > 0;
      if (!numberOk) errors.push('Número da Nota');

      // Cliente
      const cust = customer || {};
      const customerFieldLabels: Record<string, string> = {
        document: 'Documento',
        name: 'Nome',
        zipCode: 'CEP',
        address: 'Endereço',
        number: 'Número',
        district: 'Bairro',
        city: 'Cidade',
        state: 'UF',
      };
      Object.keys(customerFieldLabels).forEach((fieldKey) => {
        const val = cust?.[fieldKey];
        if (val === undefined || val === null || val.toString().trim() === '') {
          errors.push(`Cliente: ${customerFieldLabels[fieldKey]}`);
        }
      });

      // Valores
      if (!vOrig || vOrig.toString().trim() === '') errors.push('Valor Total');

      // Duplicatas
      if (!Array.isArray(dup) || dup.length === 0) {
        errors.push('Duplicatas (adicione pelo menos uma)');
      } else {
        dup.forEach((d: any, idx: number) => {
          const label = `Duplicata #${idx + 1}`;
          const hasVDup = d?.vDup !== undefined && d?.vDup !== null && !isNaN(Number(d.vDup)) && Number(d.vDup) > 0;
          if (!hasVDup) errors.push(`${label}: Valor`);
          if (!d?.dVenc) errors.push(`${label}: Data Vencimento`);
        });
      }

      // Data de emissão não pode ser no futuro
      if (issueDate && moment(issueDate).isAfter(moment())) {
        errors.push('Emissão (não pode ser no futuro)');
      }

      if (errors.length > 0) {
        allErrors.push({ id: invoice.id, fileName: invoice.fileName || 'NFS-e.pdf', errors });
      }
    });

    return allErrors;
  };

  const validadeInvoices = () => {
    const hasMissingPdf = importedInvoices.some(invoice => !invoice.pdf);
    if (hasMissingPdf) {
      addToast({
        title: "Arquivo não encontrado.",
        description: "Todas as notas selecionadas devem ter um PDF anexado.",
        type: "error",
      });
      return;
    }

    if (canEditDueDate && type === 'PRODUTO') {
      const hasPastDue = importedInvoices.some(inv =>
        inv.dup?.some(d =>
          moment(d.proxydVenc).isBefore(moment().add(15, 'days'), 'day')
        )
      );

      if (hasPastDue) {
        addToast({
          title: "Duplicata vencida",
          description: "Datas de vencimento devem ser pelo menos 15 dias a partir de hoje. Ajuste as datas para continuar.",
          type: "error",
        });
        return;
      }
    }

    if (type === 'SERVICO') {
      const errors = collectServiceInvoiceErrors();
      if (errors.length > 0) {
        const preview = errors.slice(0, 3)
          .map((e) => `${e.fileName}: ${e.errors.join(', ')}`)
          .join('\n- ');
        const extra = errors.length > 3 ? `\n... e mais ${errors.length - 3} nota(s).` : '';

        addToast({
          title: 'Campos obrigatórios incompletos',
          description: `Corrija os seguintes campos:\n- ${preview}${extra}`,
          type: 'error',
          size: 'large',
          duration: 9000,
        });
        return;
      }
    }

    requestsTotalRef.current = importedInvoices.length;
    responsesReceivedRef.current = 0;
    successInvoicesRef.current = [];

    const guid = uuidv4();

    for (const invoice of importedInvoices) {
      const formData = new FormData();

      formData.append("Type", type);
      formData.append("Key", invoice.id);
      formData.append("DestJson", JSON.stringify(invoice.dest));
      formData.append("OperationId", guid);
      formData.append("vOrig", invoice.vOrig);
      formData.append("DupJson", JSON.stringify(invoice.dup));

      if (type === 'PRODUTO') {
        formData.append("nProt", invoice.nProt);
        const xmlBlob = new Blob([atob(invoice.xml)], { type: "text/xml" });
        formData.append("xmlFile", xmlBlob, "nota.xml");
      }

      if (type === 'SERVICO') {
        formData.append("IssueDate", moment(invoice.issueDate).format("YYYY-MM-DDTHH:mm:ss"));
        formData.append("vTax", invoice.vTax || '0');
        formData.append("CustomerJson", JSON.stringify(invoice.customer));
        formData.append("VerificationCode", invoice.verificationCode);
        formData.append("Number", invoice.number);
      }

      formData.append("pdfFile", invoice.pdf);

      try {
        importInvoices(formData);
      } catch (err) {
        console.error('Erro ao validar nota:', err);
      }
    }
  }

  const handleAttachPdf = useCallback((fileId: string, pdf: File | null) => {
    setImportedInvoices((prev) =>
      prev.map((file) =>
        file.id === fileId ? { ...file, pdf } : file
      )
    );
  }, []);

  const handleUpdateDueDate = useCallback((fileId, dupNumber, newDate) => {
    setImportedInvoices(prev =>
      prev.map(f =>
        f.id === fileId
          ? {
            ...f,
            dup: f.dup.map(d =>
              d.nDup === dupNumber ? { ...d, proxydVenc: newDate } : d
            )
          }
          : f
      )
    );
  }, []);

  const handleRemoveInvoice = useCallback((fileId: string) => {
    handleDelete({ id: fileId, noRefreshData: true, showToast: false });

    setImportedInvoices(prev =>
      prev.filter(f => f.id !== fileId)
    );
  }, [handleDelete]);


  useEffect(() => {
    if ((successInvoice == null && errorInvoice == null && stateInvoice === undefined) ||
      (successInvoice && stateInvoice === undefined) ||
      (!successInvoice && errorInvoice == null))
      return;

    setReturnLoading(true);

    responsesReceivedRef.current += 1;

    if (
      successInvoice &&
      stateInvoice?.protocol &&
      !successInvoicesRef.current.includes(stateInvoice.protocol)
    ) {
      successInvoicesRef.current.push(stateInvoice.protocol);
    }

    if (responsesReceivedRef.current === requestsTotalRef.current) {
      setReturnLoading(false);

      if (successInvoicesRef.current.length > 0) {
        history.push({
          pathname: `/anticipation/duplicates`,
          state: { invoiceIds: successInvoicesRef.current },
        });
      }

      successInvoicesRef.current = [];
      responsesReceivedRef.current = 0;
    }
  }, [stateInvoice, errorInvoice]);

  return (
    <>
      <ModalLoader open={loading || loadingInvoice || loadingStatus.GET || returnLoading} text="Carregando informações" />

      <ScreenGreeting
        title="Adicionar notas fiscais"
        subTitle="Aqui você poderá adicionar notas para antecipação."
        hasBackButton
        handleBack={() => history.push({ pathname: "/anticipation" })}
      />

      <InvoiceSummary
        invoices={importedInvoices}
        limit={limit}
      />

      <Box mt={2} display="flex" flexDirection="column">
          <div>
          <Toggle
          value={type}
          onChange={(_, val) => {
            if (importedInvoices.length === 0 && val) {
              setType(val as 'PRODUTO' | 'SERVICO');
            } else if (importedInvoices.length > 0) {
              addToast({
                title: "Operação não permitida",
                description: "Para alterar o tipo de nota, remova todas as notas adicionadas.",
              });
            }
          }}
          options={[
            { label: 'Nota fiscal de produto', value: 'PRODUTO', disabled: importedInvoices.length > 0 },
            { label: 'Nota fiscal de serviço', value: 'SERVICO', disabled: importedInvoices.length > 0 },
          ]}
          />
          </div>
        {importedInvoices.length > 0 && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 1 }}>
            Para alterar o tipo de nota, remova todas as notas adicionadas.
          </Box>
        )}
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={6}
      >
        <Tooltip
          title={type === 'PRODUTO'
            ? `Importe arquivos .xml de NF-e (até ${MAX_FILES} no total).`
            : `Importe arquivos .pdf de NFS-e (até ${MAX_FILES} no total).`}
        >
          <Button
            sx={{ color: 'white' }}
            startIcon={<CloudUploadIcon />}
            variant="contained"
            component="label"
          >
            Adicionar notas
            <input
              hidden
              multiple
              aria-label={type === 'PRODUTO' ? 'Selecionar arquivos XML de notas fiscais de produto' : 'Selecionar arquivos PDF de notas fiscais de serviço'}
              accept={type === 'PRODUTO' ? '.xml' : '.pdf'}
              type="file"
              onChange={fileUpload}
            />
          </Button>
        </Tooltip>

        <Tooltip
          title={
            importedInvoices.length === 0
              ? 'Adicione notas antes de solicitar validação.'
              : type === 'SERVICO'
                ? 'Validar notas de serviço.'
                : canEditDueDate
                  ? 'Garanta vencimento das duplicatas em pelo menos 15 dias e anexe o PDF de cada nota.'
                  : 'Envie as notas para validação (é necessário anexar o PDF de cada nota).'
          }
        >
            <Button
              onClick={validadeInvoices}
              sx={{ color: 'white' }}
              variant="contained"
              endIcon={<NavigateNextIcon />}
              disabled={importedInvoices.length === 0}
            >
              Solicitar validação
            </Button>
        </Tooltip>
      </Box>

      <Box mt={1} sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
        Selecionadas: {importedInvoices.length}/{MAX_FILES}
      </Box>

      {type === 'PRODUTO' ? (
        importedInvoices.map((file, idx) => (
          <InvoiceDetail
            key={file.id || idx}
            file={file}
            canEditDueDate={canEditDueDate}
            onAttachPdf={handleAttachPdf}
            onDelete={handleRemoveInvoice}
            onUpdateDueDate={handleUpdateDueDate}
          />
        ))
      ) : (
        importedInvoices.map((file, idx) => (
          <Box key={file.id || idx} mt={2}>
            <ServiceInvoiceDetail
              key={file.id || idx}
              file={file}
              onDelete={(fileId) => {
                setImportedInvoices((prev) => prev.filter((file) => file.id !== fileId));
              }}
              onUpdate={(fileId, data) => {
                setImportedInvoices((prev) => prev.map((file) => (file.id === fileId ? { ...file, ...data } : file)));
              }}
            />
          </Box>
        ))
      )}

      {importedInvoices.length > 0 && (
        <Box display="flex" justifyContent="flex-end" alignItems="center" mt={6} mb={2}>
          <Tooltip
            title={
              importedInvoices.length === 0
                ? 'Adicione notas antes de solicitar validação.'
                : type === 'SERVICO'
                  ? 'Validar notas de serviço.'
                  : canEditDueDate
                    ? 'Garanta vencimento das duplicatas em pelo menos 15 dias e anexe o PDF de cada nota.'
                    : 'Envie as notas para validação (é necessário anexar o PDF de cada nota).'
            }
          >
            <Button
              onClick={validadeInvoices}
              sx={{ color: 'white' }}
              variant="contained"
              endIcon={<NavigateNextIcon />}
              disabled={importedInvoices.length === 0}
            >
              Solicitar validação
            </Button>
          </Tooltip>
        </Box>
      )}
    </>
  );
};

export default AddInvoice;
